import { useEffect, useState } from "react";
import type { FC } from "react";
import { CHAPTERS, HERO, MAIL, WHEN } from "./content";
import { stamp } from "./hooks";

interface BriefState {
  needs: string[];
  name: string;
  company: string;
  about: string;
  when: string;
}

const EMPTY: BriefState = { needs: [], name: "", company: "", about: "", when: "" };

/** The genie on the note: the more of the brief is written, the more of him is out. */
const GENIES = ["genie-head-1", "genie-head-1", "genie-3", "genie-2", "genie-1"].map(name => `/genie-mark/${name}.svg`);

const titles = (brief: BriefState) => CHAPTERS.filter(row => brief.needs.includes(row.id)).map(row => row.title);
const subject = (brief: BriefState) => (brief.needs.length ? `Project: ${titles(brief).join(", ")}` : "New project");

/** The brief as an email to the studio, opened in the visitor's own mail app. */
function mailto(brief: BriefState): string {
  const body = [
    `Need: ${titles(brief).join(", ") || "not sure yet"}`,
    `Name: ${brief.name}`,
    `Company: ${brief.company}`,
    `When: ${brief.when}`,
    "",
    brief.about,
  ].join("\n");
  return `mailto:${MAIL}?subject=${encodeURIComponent(subject(brief))}&body=${encodeURIComponent(body)}`;
}

interface BlankProps {
  label: string;
  value: string;
  autoComplete: string;
  onChange: (value: string) => void;
}

/** A gap in the sentence to type into; it is as wide as what is written in it. */
const Blank: FC<BlankProps> = ({ label, value, autoComplete, onChange }) => (
  <span className="sd-blank">
    <span aria-hidden>{value || label}</span>
    <input
      type="text"
      aria-label={label}
      placeholder={label}
      autoComplete={autoComplete}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </span>
);

/**
 * Start a project: the brief is one sentence with gaps, and beside it the note
 * it turns into, written as you go. The visitor sends that note themselves.
 */
export const Brief: FC = () => {
  const [brief, setBrief] = useState<BriefState>(EMPTY);
  const [today] = useState(() => stamp(new Date()));
  const put = (patch: Partial<BriefState>) => setBrief(now => ({ ...now, ...patch }));

  // A link such as /contact#models arrives with that need already picked.
  useEffect(() => {
    const row = CHAPTERS.find(item => `#${item.id}` === window.location.hash);
    if (row) setBrief(now => ({ ...now, needs: [row.id] }));
  }, []);

  const toggle = (id: string) =>
    put({ needs: brief.needs.includes(id) ? brief.needs.filter(x => x !== id) : [...brief.needs, id] });

  const done = [brief.needs.length > 0, Boolean(brief.name.trim()), Boolean(brief.about.trim()), Boolean(brief.when)];
  const count = done.filter(Boolean).length;
  const tone = CHAPTERS.find(row => brief.needs.includes(row.id))?.tone;
  const rows: Array<[string, string]> = [
    ["to", MAIL],
    ["subject", subject(brief)],
    ["need", titles(brief).join(", ")],
    ["name", brief.name],
    ["company", brief.company],
    ["when", brief.when],
  ];

  return (
    <div className="sd-ask-grid">
      <form className="sd-letter" onSubmit={e => e.preventDefault()}>
        <p>
          hello genie. i am{" "}
          <Blank label="your name" autoComplete="name" value={brief.name} onChange={name => put({ name })} /> from{" "}
          <Blank
            label="company or studio"
            autoComplete="organization"
            value={brief.company}
            onChange={company => put({ company })}
          />
          . i need{" "}
          {CHAPTERS.map(row => (
            <button
              key={row.id}
              type="button"
              className="sd-pick"
              data-tone={row.tone}
              aria-pressed={brief.needs.includes(row.id)}
              onClick={() => toggle(row.id)}
            >
              {row.title}
            </button>
          ))}
          . i want to make
        </p>
        <textarea
          aria-label="what do you want to make?"
          placeholder="a few lines are enough"
          rows={3}
          value={brief.about}
          onChange={e => put({ about: e.target.value })}
        />
        <p>
          and i need it{" "}
          {WHEN.map(option => (
            <button
              key={option}
              type="button"
              className="sd-pick"
              aria-pressed={brief.when === option}
              onClick={() => put({ when: brief.when === option ? "" : option })}
            >
              {option}
            </button>
          ))}
          .
        </p>
      </form>
      <aside className="sd-note" data-tone={tone}>
        <div className="sd-note-top">
          <img key={count} src={GENIES[count]} alt="" />
          <p>
            <span>brief / {today}</span>
            <span>{count} of 4 written</span>
          </p>
        </div>
        <dl>
          {rows.map(([key, value]) => (
            <div key={key} data-empty={value ? undefined : ""}>
              <dt>{key}</dt>
              <dd>{value || "—"}</dd>
            </div>
          ))}
        </dl>
        <p className="sd-note-body" data-empty={brief.about ? undefined : ""}>
          {brief.about || "what you want to make appears here."}
        </p>
        <a className="sd-button" href={mailto(brief)}>
          <span>open the email, ready to send</span>
          <span aria-hidden>→</span>
        </a>
        <p className="sd-note-foot">
          this opens your mail app with the note filled in. nothing is sent from this page. or write to{" "}
          <a href={`mailto:${MAIL}`}>{MAIL}</a>. {HERO.by.company} / {HERO.by.place}
        </p>
      </aside>
    </div>
  );
};
